// Attention channels — renderer-neutral grammar types (Order 15.1).
//
// Implements the Performance Grammar §1 (Attention) at the type level. Attention
// is intermittent and asymmetric: a target is not a state a character sits in, it
// is something a CHANNEL points at, briefly, then releases. Three steerable
// channels move at different speeds — body (slow), head (mid), eyes (fast and
// independent) — each pointing at a target at an engagement level.
//
// SCOPE (the decisions cluster's "v1 builds only eyes + internal-state presets;
// the field is shaped for the whole model"): these types describe the WHOLE
// model — every channel, every target kind, every engagement level — so the
// production payload and the Shot Graph can carry a full attention track today.
// The marionette engine (ops/attention) IMPLEMENTS only the cheap slice: the eyes
// channel, point + named-direction targets, resolved to a pupil offset (+ an
// optional head-joint turn). puppet / prop targets and the body channel are
// shaped here and left for a later Order. Pure types only — no runtime — so this
// bundles into the dependency-free player and runs under tsx.

// The three steerable channels, slowest → fastest (grammar §1).
export const ATTENTION_CHANNELS = ['body', 'head', 'eyes'] as const;
export type AttentionChannel = typeof ATTENTION_CHANNELS[number];

// How committed the attention is (grammar §1). Scales the offset magnitude and
// the hold: a glance barely moves and releases at once; a lock travels fully and
// holds.
export const ENGAGEMENT_LEVELS = ['glance', 'soft', 'locked'] as const;
export type EngagementLevel = typeof ENGAGEMENT_LEVELS[number];

// What a channel points at (grammar §1). Resolves to another character (@-handle),
// a prop (@-handle), a screen point (rig-canvas coords), or a NAMED EMPTY
// DIRECTION (up-recall, down-shame, …). The slice implements `point` + `direction`
// (the internal-state presets); `puppet` / `prop` are shaped for the full model.
export type AttentionTarget =
  | { kind: 'point'; x: number; y: number }      // a screen point in rig-canvas coords
  | { kind: 'puppet'; handle: string }           // @-handle of another cast member (shaped)
  | { kind: 'prop'; handle: string }             // @-handle of a prop (shaped)
  | { kind: 'direction'; name: string };         // a named empty direction (internal state)

// A single attention beat on the master clock: the channels that move, where they
// point, how committed, and how long before release. `channels` defaults to the
// eyes alone (the slice's implemented channel).
export interface AttentionBeat {
  at_seconds: number;
  target: AttentionTarget;
  channels?: AttentionChannel[];   // default ['eyes']
  engagement?: EngagementLevel;    // default 'soft'
  hold_seconds?: number;           // dwell at the target before releasing (default 0.5)
  // When true the channels do NOT return to their resting aim after the hold —
  // the look lingers (down-shame holds; an aversion that doesn't recover). When
  // false (default) the eyes break to the target then return (the aversion
  // pattern: look away, look back).
  sustain?: boolean;
}

// The named internal-state directions the slice ships (grammar §1: "an empty
// direction like up-recall or down-shame"). Each resolves to a pupil offset (in
// rig-canvas px at engagement 'locked') plus an optional head turn (deg). These
// are the cheap, body-free "thinking / feeling-bad" cases; the study names more.
export interface InternalStatePreset {
  // Pupil offset at full engagement, in rig-canvas px (the eyes' fast channel).
  pupil_dx: number;
  pupil_dy: number;
  // Optional head turn at full engagement, in degrees (the mid channel). 0 = the
  // head holds while the eyes do the work (the grammar's listener/aversion case).
  head_turn_deg: number;
  notes?: string;
}

// Seed set (grammar §1 names up-recall + down-shame explicitly; the rest follow
// the same axis). Up = recall/searching memory; down = shame/withdrawal; the
// horizontal pair are the "assemble a thought to the side" aversions. Magnitudes
// are a deliberately restrained scaffold (rig-canvas px) — the footage study
// replaces them, exactly as the Expression Library seeds await the Shock slice.
export const INTERNAL_STATE_PRESETS: Record<string, InternalStatePreset> = {
  'up-recall':    { pupil_dx: 0,   pupil_dy: -14, head_turn_deg: 0,  notes: 'eyes up, searching memory' },
  'down-shame':   { pupil_dx: 0,   pupil_dy: 12,  head_turn_deg: -6, notes: 'eyes down + a head dip — shame / withdrawal' },
  'aside-left':   { pupil_dx: -13, pupil_dy: 2,   head_turn_deg: 0,  notes: 'breaks left to assemble a thought' },
  'aside-right':  { pupil_dx: 13,  pupil_dy: 2,   head_turn_deg: 0,  notes: 'breaks right to assemble a thought' },
};

// The engagement dial → a 0..1 magnitude scale on the offset + the hold.
export const ENGAGEMENT_SCALE: Record<EngagementLevel, number> = {
  glance: 0.45,
  soft: 0.7,
  locked: 1.0,
};
